import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Alert,
  AutoComplete,
  Form,
  Input,
  Select,
  Spin,
  message,
  Drawer,
  Button,
  Checkbox,
  Collapse,
  Tooltip,
} from 'antd';
import DatePicker from '../../components/DatePicker';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import {
  User,
  FileText,
  ListChecks,
  ListBullets,
  Folder,
  ClipboardText,
  FolderSimplePlus,
} from '@phosphor-icons/react';
import axios from 'axios';
import PatientSearchPanel from '../../components/patient-search/PatientSearchPanel';
import EmrProviderAutocomplete from '../../components/autocomplete/EmrProviderAutocomplete';
import EhrPhoneInput from './EhrPhoneInput';
import NextgenDocumentMetadataFields from '../../components/nextgen/NextgenDocumentMetadataFields';
import useEmrPatientSearch from '../../hooks/useEmrPatientSearch';
import { createChartUpload } from '../../actions/records';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../../components/chartActions/replyTiffWarning';
import { getDocumentCategories } from '../../actions/userProfile';
import { captureAnalytics } from '../../actions/preload';
import dedupeDocumentCategories from '../../utils/dedupeDocumentCategories';
import {
  hasPresentValue, normalizeFormValues, normalizePhoneDigits, validateCrossFieldRules,
} from '../../util/ehr';
import { getFieldFlexStyle, getFieldWidthClass, getRowClass } from '../../util/fieldGrouping';
import {
  derivePatientIdentifierDisplay,
  extractPatientDob,
  extractPatientMrn,
  getSectionIconType,
  getWorkflowIconType,
  matchesDobField,
  parseDateFieldValue,
  resolveFieldSlots,
} from './dynamicEhrDrawerHelpers';
import './dynamicEhrDrawer.scss';

const { TextArea } = Input;

// Date spellings each prefill path accepts. Deliberately different lists: the
// patient-search mapping has only ever seen ISO and US dates, while the schema
// prefill also accepts a bare YYYY-MM-DD. Kept separate so the shared parser
// does not silently widen either one.
const PATIENT_DATE_FORMATS = [moment.ISO_8601, 'MM/DD/YYYY'];
const PREFILL_DATE_FORMATS = [moment.ISO_8601, 'MM/DD/YYYY', 'YYYY-MM-DD'];

const generateIdempotencyKey = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const applyPatientFallbacks = (patient, baseUpdates, fieldDefinitions, identifierOverridesRef) => {
  if (!patient) {
    return baseUpdates;
  }

  const updates = { ...baseUpdates };

  const assignValueToMatchingFields = (matcher, rawValue) => {
    if (rawValue === undefined || rawValue === null) {
      return;
    }

    const valueString = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    if (valueString === '') {
      return;
    }

    Object.values(fieldDefinitions).forEach((field) => {
      if (!matcher(field)) {
        return;
      }
      if (updates[field.internal_name] !== undefined) {
        return;
      }

      if (field.component === 'date') {
        updates[field.internal_name] = parseDateFieldValue(valueString, PATIENT_DATE_FORMATS);
        return;
      }

      updates[field.internal_name] = valueString;
    });
  };

  // Apply DOB fallback if not already mapped
  assignValueToMatchingFields(matchesDobField, extractPatientDob(patient));

  if (updates.patient_identifier === undefined) {
    const patientId = patient?.value;
    const mrnValue = extractPatientMrn(patient);
    const displayValue = derivePatientIdentifierDisplay(patientId, mrnValue);

    if (displayValue !== undefined && displayValue !== null) {
      updates.patient_identifier = displayValue;
      if (identifierOverridesRef?.current && patientId !== undefined && patientId !== null) {
        identifierOverridesRef.current.patient_identifier = {
          id: patientId,
          display: displayValue,
        };
      }
    }
  }

  return updates;
};

const clearValuesForFields = (fieldValues) => Object.keys(fieldValues || {}).reduce((acc, key) => {
  acc[key] = undefined;
  return acc;
}, {});

// Map section titles to appropriate icons
const getSectionIcon = (sectionTitle) => {
  switch (getSectionIconType(sectionTitle)) {
    case 'patient':
      return <User size={18} weight="bold" />;
    case 'document':
      return <FileText size={18} weight="bold" />;
    case 'folder':
      return <Folder size={18} weight="bold" />;
    default:
      return <ListBullets size={18} weight="bold" />;
  }
};

// Get icon for the drawer title based on workflow type
const getWorkflowIcon = (workflow, title) => {
  if (getWorkflowIconType(workflow, title) === 'task') {
    return <ListChecks size={24} weight="fill" />;
  }
  if (getWorkflowIconType(workflow, title) === 'create') {
    return <FolderSimplePlus size={24} weight="fill" />;
  }
  // Default to Send to Chart icon
  return <ClipboardText size={24} weight="fill" />;
};

const WORKFLOW_SUPPORT_KEYS = {
  create_chart: 'create_chart',
  send_to_chart: 'send_to_chart',
  task_creation: 'task_creation',
};

const WORKFLOW_LABELS = {
  create_chart: 'Create Chart',
  send_to_chart: 'Send to Chart',
  task_creation: 'Create Task',
};

const TASK_EMBEDDING_WORKFLOWS = ['send_to_chart', 'create_chart'];

const isTaskSchemaEntry = (entry) => (
  entry?.task_section === true || entry?.task_section === 'true'
);

// A checkbox puts its label inside antd's own <label>, so this renders the help
// icon as a real button: browsers skip label activation when the click lands on
// interactive content, so the icon does not toggle the box, and it stays
// keyboard reachable. Only the checkbox branch needs it — every other field's
// help icon sits in the Form.Item label, outside any control.
const renderCheckboxHelpIcon = (helpText) => (
  <Tooltip title={helpText} getPopupContainer={() => document.body}>
    <button
      type="button"
      className="dynamic-ehr-drawer__help-icon dynamic-ehr-drawer__help-icon--inline"
      aria-label={helpText}
    >
      &#9432;
    </button>
  </Tooltip>
);

// Schema component that renders the NextGen document-type picker and the
// metadata fields that type declares (see NextgenDocumentMetadataFields).
const NEXTGEN_METADATA_COMPONENT = 'nextgen_document_metadata';

// Components the drawer renders itself. Anything else in a schema is a field
// this build does not know how to display, and rendering it as a plain text
// input would silently submit the wrong shape.
const KNOWN_FREE_TEXT_COMPONENTS = ['text', undefined, null];

// Components with their own branch in renderField's switch. Together with
// KNOWN_FREE_TEXT_COMPONENTS (which the default branch accepts) this is
// everything this build can display.
const RENDERED_COMPONENTS = [
  'textarea', 'checkbox', 'date', 'select', 'multi_select', 'emr_provider_autocomplete',
  'hidden', NEXTGEN_METADATA_COMPONENT,
];

// A NextGen practice list narrowed to nothing is not an empty dropdown to shrug
// at: nothing can be uploaded until a Super Admin approves a catalog, so say so
// in the same terms the metadata editor uses for its own dead ends.
const NEXTGEN_PRACTICE_EMPTY_STATE = 'No practices are ready for upload. Ask a Super Admin to refresh and approve a practice catalog.';

const isHiddenField = (field) => (
  field?.component === 'hidden' || field?.hidden === true || field?.hidden === 'true'
);

const schemaEntryRows = (entry) => (
  (entry?.subsections || []).flatMap((subsection) => subsection.rows || [])
);

const fieldsFromRows = (rows = []) => rows.flatMap((row) => (
  (row || []).filter(Boolean)
));

const taskFieldsForSections = (sections = []) => {
  const fields = [];

  sections.forEach((section) => {
    if (isTaskSchemaEntry(section)) {
      fields.push(...fieldsFromRows(schemaEntryRows(section)));
      return;
    }

    (section.subsections || []).forEach((subsection) => {
      if (isTaskSchemaEntry(subsection)) {
        fields.push(...fieldsFromRows(subsection.rows || []));
      }
    });
  });

  return fields;
};

const visibleRowsForSubsection = (subsection) => (
  (subsection.rows || [])
    .map(row => (row || []).filter(field => !isHiddenField(field)))
    .filter(row => row.length > 0)
);

const taskFieldNamesForSections = (sections = []) => {
  const names = new Set();

  taskFieldsForSections(sections).forEach((field) => {
    if (field.internal_name) {
      names.add(field.internal_name);
    }
  });

  return names;
};

const requiredFieldsForSections = (sections = []) => (
  sections.flatMap((section) => (
    (section.subsections || []).flatMap((subsection) => (
      (subsection.rows || []).flatMap((row) => (
        (row || []).filter(field => field?.required && field.internal_name && !isHiddenField(field))
      ))
    ))
  ))
);

const credentialSupportsWorkflow = (credential, workflow) => {
  const requiredWorkflow = WORKFLOW_SUPPORT_KEYS[workflow];
  if (!requiredWorkflow) return true;

  return Array.isArray(credential?.supports_workflows)
    && credential.supports_workflows.includes(requiredWorkflow);
};

const mergeMappedPatientSearchValues = (payload, values, fieldMappings) => (
  Object.keys(fieldMappings || {}).reduce((mergedPayload, fieldName) => {
    if (Object.prototype.hasOwnProperty.call(payload, fieldName)) {
      return mergedPayload;
    }

    const value = values[fieldName];
    if (!hasPresentValue(value)) {
      return mergedPayload;
    }

    return {
      ...mergedPayload,
      [fieldName]: moment.isMoment(value) ? value.format('YYYY-MM-DD') : value,
    };
  }, payload)
);

// A field whose options come from a closed, authoritative live dictionary
// (options_strict). Organization categories are not valid substitutes, and a
// prefilled value is only valid if it exists in that source. This is a property
// of the field (set in the schema), not of any one EMR.
const usesAuthoritativeOptions = field => Boolean(field.options_source && field.options_strict);

const matchingOptionValue = (options, value) => {
  const normalizedValue = String(value || '').trim().toLowerCase();
  const match = options.find(option => String(option.value || '').trim().toLowerCase() === normalizedValue);

  return match?.value;
};

// A field with suggestions (schema key `suggested_options`) offers the options
// whose metadata.category matches the current value of the field it depends on
// — e.g. document_title suggesting the titles configured for the selected
// document_category — while still accepting free text. Returns null when the
// field has no suggestions config, [] when the dependency is unset or has no
// configured options (both mean "render as plain free text"), and a non-empty
// list to render an autocomplete.
const suggestedOptionsFor = (field, optionsBySource, dependencyValue) => {
  const config = field.suggested_options;
  if (!config) return null;
  if (!dependencyValue) return [];

  const sourceOptions = optionsBySource[config.options_source] || [];
  const normalized = String(dependencyValue).trim().toLowerCase();

  return sourceOptions.filter(
    (option) => String(option.metadata?.category ?? '').trim().toLowerCase() === normalized,
  );
};

const DynamicEhrDrawer = ({ visible, drawerData, onClose, createChartUpload, getDocumentCategories, allDocumentCategories }) => {
  const [form] = Form.useForm();
  const [schema, setSchema] = useState(null);
  const [options, setOptions] = useState({});
  const [optionMetadata, setOptionMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});
  // Synchronous reentrancy guard: `submitting` state only blocks clicks after
  // a re-render, leaving a double-click window during async validation. A
  // duplicate submit here means a duplicate patient chart in the EHR.
  const submittingRef = useRef(false);
  const [credentials, setCredentials] = useState([]);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [fetchingCredentials, setFetchingCredentials] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const patientIdentifierOverridesRef = useRef({});
  const patientSearchResetRef = useRef(() => {});
  const [documentCategories, setDocumentCategories] = useState([]);
  const [flashingFields, setFlashingFields] = useState({});
  const [crossFieldErrors, setCrossFieldErrors] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [dynamicLoading, setDynamicLoading] = useState({});
  const [taskRequested, setTaskRequested] = useState(false);
  const idempotencyKeyRef = useRef(null);

  // Single write path for programmatic form updates: antd's form store is the
  // source of truth for validation, but `submitDisabled`/`missingRequiredVisibleField`
  // read the `formValues` mirror, and `form.setFieldsValue` does not fire
  // `onValuesChange`. Funnel both writes through here so the store and the mirror
  // can never drift (a missed mirror would silently break submit-button gating).
  const applyFieldValues = useCallback((values) => {
    form.setFieldsValue(values);
    setFormValues(prev => ({ ...prev, ...values }));
  }, [form]);

  const {
    emrIntegrationCredentialId,
    workflow = 'send_to_chart',
    prefill: incomingPrefill,
    parentType,
    sendToken,
    providerHints,
  } = drawerData || {};

  const modalTitle = drawerData?.title || 'Send to Chart';

  const prefill = useMemo(() => incomingPrefill || {}, [incomingPrefill]);
  const patientSearchConfig = schema?.metadata?.patient_search;
  const allowResultSelection = patientSearchConfig?.allow_result_selection !== false;
  const requiresPatientResultSelection = Boolean(
    patientSearchConfig?.require_result_selection === true && allowResultSelection,
  );

  const getSubmissionIdempotencyKey = useCallback(() => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = generateIdempotencyKey();
    }

    return idempotencyKeyRef.current;
  }, []);

  // Fetch document categories when drawer opens
  useEffect(() => {
    if (!visible) return;

    const fetchDocumentCategories = async () => {
      try {
        await getDocumentCategories();
      } catch (error) {
        console.error('Failed to fetch document categories', error);
      }
    };

    fetchDocumentCategories();
  }, [visible, getDocumentCategories]);

  // Update local state when document categories are loaded from Redux
  useEffect(() => {
    if (allDocumentCategories?.categories) {
      setDocumentCategories(allDocumentCategories.categories);
    }
  }, [allDocumentCategories]);

  // A schema component this build cannot render is a deploy-skew fact about the
  // schema, so report it once when the schema arrives. renderField's fallback
  // Alert still tells the user; warning from there repeated the message on every
  // keystroke that re-rendered the drawer.
  useEffect(() => {
    if (!schema) return;

    const unsupported = Array.from(new Set(
      (schema.sections || [])
        .flatMap(schemaEntryRows)
        .flatMap((row) => (row || []).filter(Boolean))
        .map((field) => field.component)
        .filter((component) => (
          !RENDERED_COMPONENTS.includes(component) && !KNOWN_FREE_TEXT_COMPONENTS.includes(component)
        )),
    ));

    unsupported.forEach((component) => {
      console.warn(`Unsupported EHR schema component: ${component}`);
    });
  }, [schema]);

  // Fetch credentials when drawer opens
  useEffect(() => {
    if (!visible || !drawerData) return;

    const fetchCredentials = async () => {
      try {
        const response = await axios.get('/api/v1/emr_integration_credentials');
        const creds = response.data.credentials || [];
        const eligibleCredentials = emrIntegrationCredentialId
          ? creds.filter((credential) => String(credential.id) === String(emrIntegrationCredentialId))
          : creds;
        const workflowCredentials = eligibleCredentials.filter((credential) => (
          credentialSupportsWorkflow(credential, workflow)
        ));
        setCredentials(workflowCredentials);

        // Auto-select first (and only) credential per assumption
        if (workflowCredentials.length > 0) {
          setSelectedCredential(workflowCredentials[0]);
        } else {
          const workflowLabel = WORKFLOW_LABELS[workflow] || 'this workflow';
          const errorMessage = eligibleCredentials.length > 0
            ? `No EMR credential supports ${workflowLabel} for this organization`
            : 'No EMR credentials found for this organization';
          message.error(errorMessage);
          onClose();
        }
      } catch (error) {
        console.error('Failed to fetch credentials', error);
        message.error('Unable to load EMR credentials');
        onClose();
      } finally {
        setFetchingCredentials(false);
      }
    };

    fetchCredentials();
  }, [visible, drawerData, emrIntegrationCredentialId, workflow, onClose]);

  // Reset state after drawer close animation completes
  const handleAfterVisibleChange = useCallback((isVisible) => {
    if (!isVisible) {
      setSchema(null);
      setOptions({});
      setOptionMetadata({});
      setLoading(true);
      setSubmitting(false);
      setFormValues({});
      setCredentials([]);
      setSelectedCredential(null);
      setFetchingCredentials(true);
      setSelectedPatient(null);
      setFlashingFields({});
      setCrossFieldErrors([]);
      setDynamicOptions({});
      setDynamicLoading({});
      setTaskRequested(false);
      patientIdentifierOverridesRef.current = {};
      idempotencyKeyRef.current = null;
      form.resetFields();
    }
  }, [form]);

  // Fetch schema when credential is selected
  useEffect(() => {
    // Don't fetch schema until we have a credential and drawer is visible
    if (!visible || !selectedCredential) {
      return;
    }

    const fetchSchema = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/v1/emr_ui_schemas', {
          params: {
            emr_integration_credential_id: selectedCredential.id,
            workflow,
            parent_type: parentType,
            send_token: sendToken,
          },
        });
        const schemaResponse = response.data;
        setSchema(schemaResponse);
        setTaskRequested(false);

        const prefetchedOptions = schemaResponse?.options || {};
        const patientSearchSource = schemaResponse?.metadata?.patient_search?.options_source;

        // Extract all fields from nested structure to get options sources
        const allFieldsForOptions = [];
        (schemaResponse.sections || []).forEach((section) => {
          (section.subsections || []).forEach((subsection) => {
            (subsection.rows || []).forEach((row) => {
              allFieldsForOptions.push(...row);
            });
          });
        });

        const sources = Array.from(new Set(
          [
            ...allFieldsForOptions
              .filter(f => !f.dynamic_options_source)
              .map(field => field.options_source),
            // Suggestion fields prefetch their (whole) option source up front,
            // then filter it client-side by the dependency value at render time.
            ...allFieldsForOptions
              .map((field) => field.suggested_options?.options_source),
            patientSearchSource,
          ].filter(Boolean),
        ));

        const sourcesToFetch = sources.filter(source => !prefetchedOptions[source]);
        let fetchedOptions = {};
        let fetchedOptionMetadata = {};

        if (sourcesToFetch.length) {
          const fetchOptions = await Promise.all(sourcesToFetch.map(async (source) => {
            const res = await axios.get('/api/v1/emr_options', {
              params: {
                emr_integration_credential_id: selectedCredential.id,
                source,
              },
            });
            return [source, res.data.options || [], res.data];
          }));
          fetchedOptions = Object.fromEntries(fetchOptions.map(([source, sourceOptions]) => [source, sourceOptions]));
          fetchedOptionMetadata = Object.fromEntries(fetchOptions.map(([source, , data]) => [source, data]));
        }
        setOptionMetadata(fetchedOptionMetadata);

        const allOptions = { ...prefetchedOptions, ...fetchedOptions };
        setOptions(allOptions);

        form.resetFields();

        // Extract all fields from nested sections/subsections/rows and apply prefill
        const initialValues = {};
        const resolvedPrefill = {
          ...(schemaResponse.prefill || {}),
          ...prefill,
        };
        (schemaResponse.sections || []).forEach((section) => {
          (section.subsections || []).forEach((subsection) => {
            (subsection.rows || []).forEach((row) => {
              row.forEach((field) => {
                const value = resolvedPrefill[field.internal_name];
                // A blank prefill is the absence of a value, not a value: it must
                // fall through to auto_select_single/default handling below
                // rather than pinning the field to ''.
                if (hasPresentValue(value)) {
                  const fieldOptions = field.options_source ? (allOptions[field.options_source] || []) : [];
                  const authoritative = usesAuthoritativeOptions(field);
                  const authoritativeValue = authoritative
                    ? matchingOptionValue(fieldOptions, value)
                    : undefined;

                  if (authoritative && authoritativeValue === undefined) {
                    // Value isn't in the authoritative source. If the source
                    // simply hasn't loaded, warn so the drop is diagnosable;
                    // otherwise it's a genuine not-in-dictionary value we must
                    // drop rather than submit an invalid one.
                    if (!fieldOptions.length) {
                      console.warn(`Prefill skipped for ${field.internal_name}: authoritative options (${field.options_source}) not loaded`);
                    }
                    return;
                  }

                  // Convert date strings to moment objects for date fields
                  if (field.component === 'date') {
                    initialValues[field.internal_name] = parseDateFieldValue(value, PREFILL_DATE_FORMATS);
                  } else if (field.input_mask === 'phone') {
                    initialValues[field.internal_name] = normalizePhoneDigits(value);
                  } else if (authoritative) {
                    initialValues[field.internal_name] = authoritativeValue;
                  } else {
                    initialValues[field.internal_name] = value;
                  }
                } else if (field.options_source) {
                  const fieldOptions = allOptions[field.options_source] || [];
                  if (field.auto_select_single && fieldOptions.length === 1) {
                    // Auto-select if only one option available
                    initialValues[field.internal_name] = fieldOptions[0].value;
                  } else {
                    // Pre-select option marked as default by the backend
                    const defaultOption = fieldOptions.find(opt => opt.metadata?.is_default);
                    if (defaultOption) {
                      initialValues[field.internal_name] = defaultOption.value;
                    }
                  }
                } else if (field.default_value !== undefined) {
                  initialValues[field.internal_name] = field.default_value;
                }
              });
            });
          });
        });
        form.setFieldsValue(initialValues);
        setFormValues(initialValues);
      } catch (error) {
        console.error(error);
        message.error('Unable to load EMR schema for this integration.');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [visible, selectedCredential, workflow, onClose, form, prefill, parentType, sendToken]);

  // Stable provider hint strings to avoid re-triggering the match effect
  const orderingName = providerHints?.orderingProviderName;
  const referringName = providerHints?.referringProviderName;

  const handleDynamicOptionsChange = useCallback(async (changedValues) => {
    if (!schema) return;

    const allFields = [];
    (schema.sections || []).forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          allFields.push(...row);
        });
      });
    });

    const dynamicFields = allFields.filter(f => f.dynamic_options_source);

    for (const field of dynamicFields) {
      const { depends_on, mapping } = field.dynamic_options_source;
      if (!(depends_on in changedValues)) continue;

      const selectedValue = changedValues[depends_on];

      // Clear the dependent field value when its dependency changes
      applyFieldValues({ [field.internal_name]: undefined });

      if (!selectedValue || !mapping[selectedValue]) {
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: [] }));
        setDynamicLoading(prev => ({ ...prev, [field.internal_name]: false }));
        continue;
      }

      const source = mapping[selectedValue];

      // Use cached options if already fetched (length check avoids caching empty results permanently)
      if (options[source]?.length) {
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: options[source] }));
        continue;
      }

      // Fetch from API — capture the dependency value to detect stale responses
      const expectedValue = selectedValue;
      setDynamicLoading(prev => ({ ...prev, [field.internal_name]: true }));
      try {
        const res = await axios.get('/api/v1/emr_options', {
          params: {
            emr_integration_credential_id: selectedCredential.id,
            source,
          },
        });

        // Guard against stale responses: only apply if the dependency hasn't changed
        const currentValue = form.getFieldValue(depends_on);
        if (currentValue !== expectedValue) return;

        const fetched = res.data.options || [];
        setOptions(prev => ({ ...prev, [source]: fetched }));
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: fetched }));
      } catch (err) {
        // Ignore errors from stale requests
        const currentValue = form.getFieldValue(depends_on);
        if (currentValue !== expectedValue) return;

        console.error(`Failed to fetch dynamic options for ${source}:`, err);
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: [] }));
        const apiDetail = err?.response?.data?.error || err?.response?.data?.detail;
        const errorMsg = apiDetail || `Failed to load ${field.label || 'options'}. This feature may not be enabled in your EMR.`;
        message.warning(errorMsg);
        form.setFields([{ name: field.internal_name, errors: [errorMsg] }]);
      } finally {
        const currentValue = form.getFieldValue(depends_on);
        if (currentValue !== expectedValue) return;
        setDynamicLoading(prev => ({ ...prev, [field.internal_name]: false }));
      }
    }
  }, [schema, form, options, selectedCredential]);

  // Clears a suggestion field's value when the field its suggestions depend on
  // changes, so a title picked for the previous category doesn't silently carry
  // over once a new category is selected (mirrors the dependent-dropdown
  // clearing above). The user can still retype it — the field accepts free text.
  // Only values taken from the suggestion source are cleared, and only when the
  // newly selected category doesn't also suggest them. Free text is the user's
  // own input and survives category changes as it always has — including when
  // the feature is off or no category involved has configured options.
  const clearSuggestedFieldsOnDependencyChange = useCallback((changedValues) => {
    if (!schema) return;

    (schema.sections || []).forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          row.forEach((field) => {
            const config = field.suggested_options;
            if (!config || !(config.depends_on in changedValues)) return;

            const currentValue = form.getFieldValue(field.internal_name);
            if (!currentValue) return;

            const sourceOptions = options[config.options_source] || [];
            if (!matchingOptionValue(sourceOptions, currentValue)) return;

            const newCategoryOptions = suggestedOptionsFor(field, options, changedValues[config.depends_on]) || [];
            if (matchingOptionValue(newCategoryOptions, currentValue)) return;

            applyFieldValues({ [field.internal_name]: undefined });
          });
        });
      });
    });
  }, [schema, form, options, applyFieldValues]);

  const handleValuesChange = useCallback((changedValues, allValues) => {
    setFormValues(allValues || form.getFieldsValue(true));
    handleDynamicOptionsChange(changedValues);
    clearSuggestedFieldsOnDependencyChange(changedValues);

    if (!crossFieldErrors.length) return;

    const changedKeys = Object.keys(changedValues);
    const shouldClear = crossFieldErrors.some(
      (err) => err.fields.some((f) => changedKeys.includes(f)),
    );

    if (shouldClear) {
      setCrossFieldErrors([]);
      const fieldsToClear = crossFieldErrors.flatMap(
        (err) => err.fields.map((field) => ({ name: field, errors: [] })),
      );
      form.setFields(fieldsToClear);
    }
  }, [crossFieldErrors, form, handleDynamicOptionsChange, clearSuggestedFieldsOnDependencyChange]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      if (!schema || !selectedCredential) return;

      // Cross-field validation
      const crossErrors = validateCrossFieldRules(
        schema.metadata?.validations,
        values,
      );
      if (crossErrors.length) {
        setCrossFieldErrors(crossErrors);
        const fieldErrors = crossErrors.flatMap(
          (err) => err.fields.map((field) => ({ name: field, errors: [err.message] })),
        );
        form.setFields(fieldErrors);
        return;
      }

      // Extract all fields from nested structure for normalization
      const allFields = [];
      (schema.sections || []).forEach((section) => {
        (section.subsections || []).forEach((subsection) => {
          (subsection.rows || []).forEach((row) => {
            allFields.push(...row);
          });
        });
      });

      const payload = mergeMappedPatientSearchValues(
        normalizeFormValues(allFields, values),
        values,
        patientSearchConfig?.field_mappings,
      );
      const taskFieldNames = taskFieldNamesForSections(schema.sections || []);
      const submitWithTask = TASK_EMBEDDING_WORKFLOWS.includes(workflow)
        && taskRequested
        && taskFieldNames.size > 0;

      if (TASK_EMBEDDING_WORKFLOWS.includes(workflow) && !submitWithTask) {
        taskFieldNames.forEach((fieldName) => {
          delete payload[fieldName];
        });
      }

      Object.entries(patientIdentifierOverridesRef.current || {}).forEach(([fieldName, meta]) => {
        if (!meta) {
          return;
        }

        const { id, display } = meta;
        if (id === undefined || id === null) {
          return;
        }

        const submittedValueExists = Object.prototype.hasOwnProperty.call(values, fieldName);
        if (display !== undefined && display !== null && submittedValueExists) {
          const currentValue = values[fieldName];
          if (currentValue === undefined || currentValue === null) {
            return;
          }

          if (String(currentValue) !== String(display)) {
            return;
          }
        }

        payload[fieldName] = id;
      });

      const patientIdentifierOverride = patientIdentifierOverridesRef.current?.patient_identifier;
      const selectedPatientIdentifierSubmitted = selectedPatient
        && hasPresentValue(patientIdentifierOverride?.id)
        && String(payload.patient_identifier) === String(patientIdentifierOverride.id);

      if (requiresPatientResultSelection && !selectedPatientIdentifierSubmitted) {
        message.error('Select a patient from search results before submitting.');
        return;
      }

      // Map workflow to upload_type
      const workflowToUploadType = {
        create_chart: submitWithTask ? 'chart_and_task' : 'chart_creation',
        send_to_chart: submitWithTask ? 'upload_and_task' : 'auto_upload',
        task_creation: 'task_creation',
      };
      const uploadType = workflowToUploadType[workflow] || 'auto_upload';

      // Clear any existing messages before making the request
      message.destroy();

      const chartUploadRequest = {
        emrIntegrationCredentialId: selectedCredential.id,
        data: payload,
        uploadType,
        parentType,
        sendToken,
        idempotencyKey: getSubmissionIdempotencyKey(),
      };

      const result = await createChartUpload(chartUploadRequest);

      // Check if the action returned an error
      if (result && result.error) {
        idempotencyKeyRef.current = null;
        // Prefer the server's own copy. requestTransport only promotes a derived
        // message into top-level `message` when the body carried `data.errors`
        // (plural array); the reply chart endpoints answer with `{error: "..."}`
        // (singular), so `result.message` would stay as axios' generic
        // "Request failed with status code 422" and the actionable ENG-3012 text
        // ("Reply has no uploadable document", "TIFF reply attachments are not
        // currently supported for chart upload.", "A document could not be
        // prepared for chart upload.") would never reach the user.
        const errorMessage = result.response?.data?.error
          || result.message
          || 'Unable to queue chart upload.';
        message.error(errorMessage);
        return;
      }

      // ENG-3012 TIFF deferral: partial success (visible TIFFs excluded from a
      // reply filing) must not disappear behind the ordinary success toast --
      // the drawer closes only once the warning is acknowledged.
      const tiffWarnings = extractReplyTiffWarnings(result);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({ warnings: tiffWarnings, workflow, onAcknowledge: handleClose });
        return;
      }

      const queuedMessage = workflow === 'task_creation'
        ? 'Task queued. We will notify you of any issues.'
        : 'Chart upload queued. We will notify you of any issues.';
      message.success(queuedMessage);
      handleClose();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      const responseErrors = error?.response?.data;
      const errorMessage = responseErrors?.errors?.join(', ') || responseErrors?.error || 'Unable to queue chart upload.';
      idempotencyKeyRef.current = null;
      message.error(errorMessage);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const fieldDefinitions = useMemo(() => {
    const map = {};

    // Extract all fields from nested sections/subsections/rows
    (schema?.sections || []).forEach(section => {
      (section.subsections || []).forEach(subsection => {
        (subsection.rows || []).forEach(row => {
          row.forEach(field => {
            map[field.internal_name] = field;
          });
        });
      });
    });

    return map;
  }, [schema]);

  // Auto-match AI-extracted provider names to an EmrProvider and pre-populate the field
  useEffect(() => {
    if (!visible || loading || !schema) return;
    if (!orderingName && !referringName) return;

    const hasProviderField = Object.values(fieldDefinitions).some(
      (f) => f.internal_name === 'provider_id' && f.component === 'emr_provider_autocomplete'
    );
    if (!hasProviderField) return;

    const currentValue = form.getFieldValue('provider_id');
    if (currentValue) return;

    let cancelled = false;
    const controller = new AbortController();

    const matchProvider = async () => {
      try {
        const response = await axios.post('/api/v1/emr_providers/match', {
          ordering_provider_name: orderingName,
          referring_provider_name: referringName,
        }, {
          signal: controller.signal,
          headers: {
            'X-CSRF-Token': document.getElementsByName('csrf-token')[0]?.content,
          },
        });

        const { matched, provider } = response.data;
        if (cancelled) return;

        // Re-check before setting — user may have selected a provider while request was in-flight
        if (matched && provider?.id && !form.getFieldValue('provider_id')) {
          applyFieldValues({ provider_id: provider.id });
          setFlashingFields((prev) => ({ ...prev, provider_id: true }));
          setTimeout(() => {
            setFlashingFields((prev) => {
              const next = { ...prev };
              delete next.provider_id;
              return next;
            });
          }, 700);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.warn('Provider matching failed:', error);
        }
      }
    };

    matchProvider();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, loading, schema, orderingName, referringName, fieldDefinitions, form]);

  const getFieldOptions = useCallback((field) => {
    // Check for dynamically-loaded options (e.g., delegate_id depends on delegate_type)
    if (field.dynamic_options_source && dynamicOptions[field.internal_name]) {
      return dynamicOptions[field.internal_name];
    }

    const inlineOptions = field.options || [];
    const sourceOptions = field.options_source ? (options[field.options_source] || []) : [];

    if (inlineOptions.length) {
      return inlineOptions;
    }

    // Authoritative-source fields (options_strict, e.g. Veradigm Unity
    // dictionary entries) are never Medsender organization categories: always
    // use the fetched source options (even when empty) so they never fall
    // through to the Redux category mapping below.
    if (usesAuthoritativeOptions(field)) {
      return sourceOptions;
    }

    if (field.options_source && sourceOptions.length) {
      return sourceOptions;
    }

    // Prefer Redux state for legacy document-category schemas, but fall
    // through to fetched source options when the store is empty (failed or
    // pending fetch, org without categories) so a required select never
    // renders empty while valid options exist.
    if (field.internal_name === 'document_category') {
      const mapped = dedupeDocumentCategories(documentCategories)
        .map((category) => ({
          value: category.emr_category,
          label: category.emr_category,
        }));
      if (mapped.length) return mapped;
    }

    return sourceOptions;
  }, [documentCategories, options, dynamicOptions]);

  // Some schema-driven selects can describe an automatic default derived from
  // another field. The rule map is supplied as metadata on the field's existing
  // options source; keeping this declarative avoids coupling the drawer to a
  // particular EMR or routing implementation.
  const autoAssignPlaceholderFor = useCallback((field) => {
    const config = field.auto_assign_placeholder;
    if (!config) return undefined;

    const category = formValues[config.category_field];
    if (category === undefined || category === null || category === '') return undefined;

    const rules = optionMetadata[config.options_source]?.[config.response_key];
    const rule = rules?.[category];
    const assignees = Array.isArray(rule?.assignees) ? rule.assignees.filter(Boolean) : [];
    if (!rule || assignees.length === 0) return undefined;

    return rule.mode === 'round_robin'
      ? 'Auto-assign (round-robin)'
      : `Auto-assign (${assignees[0]})`;
  }, [formValues, optionMetadata]);

  const renderFieldLabel = (field) => {
    if (!field.label) {
      return null;
    }

    const helpText = field.help_text?.trim();
    if (helpText) {
      return (
        <span className="dynamic-ehr-drawer__field-label">
          {field.label}
          {' '}
          <Tooltip title={helpText} getPopupContainer={() => document.body}>
            <span className="dynamic-ehr-drawer__help-icon">&#9432;</span>
          </Tooltip>
        </span>
      );
    }

    return (
      <span className="dynamic-ehr-drawer__field-label">
        {field.label}
      </span>
    );
  };

  // `slot` is the field this one adopted, if the schema declared a pairing. Only
  // a component that knows what to do with it reads it.
  const renderField = (field, { slot } = {}) => {
    const {
      internal_name: name,
      label,
      component,
      required,
      help_text: helpText,
      width,
      validation,
    } = field;

    if (isHiddenField(field)) {
      return (
        <Form.Item key={name} name={name} noStyle preserve={false}>
          <Input type="hidden" />
        </Form.Item>
      );
    }

    const fieldOptions = getFieldOptions(field);

    // `hide_when_auto_selected`: once the option list has collapsed to a single
    // choice the prefill pass already made, showing a one-entry dropdown asks
    // the user to re-make a decision they have no say in. A property of the
    // field (set in the schema), not of any one EMR.
    //
    // The held value must be that sole option. A prefill that named something
    // else — a stale fax-derived location, an option the source has since
    // dropped — is a value the user still needs to see and correct, so the field
    // stays visible rather than hiding a mismatch behind a hidden input.
    if (
      field.hide_when_auto_selected
      && fieldOptions.length === 1
      && hasPresentValue(formValues[name])
      && formValues[name] === fieldOptions[0].value
    ) {
      return (
        <Form.Item key={name} name={name} noStyle preserve>
          <Input type="hidden" />
        </Form.Item>
      );
    }

    const fieldClassNames = ['dynamic-ehr-drawer__field'];

    // Add width-based CSS class
    const widthClass = getFieldWidthClass(width);
    fieldClassNames.push(widthClass);

    // Add flash class if field was just updated
    if (flashingFields[name]) {
      fieldClassNames.push('field-flash');
    }

    // The NextGen practice selector is fed by a filtered catalog: a practice
    // only appears once it can actually serve a document type. Zero options is
    // therefore a configuration state with a known remedy, not a transient
    // empty list (the drawer renders a spinner, not fields, while options load),
    // so the remediation replaces the dropdown. The field keeps its form binding
    // so a required practice still blocks submission.
    if (
      schema?.emr === 'NextGen'
      && component === 'select'
      && name === 'location'
      && fieldOptions.length === 0
    ) {
      return (
        <div key={name} className={fieldClassNames.join(' ')} style={{ flex: getFieldFlexStyle(width) }}>
          <Form.Item
            name={name}
            noStyle
            preserve={false}
            rules={required ? [{ required: true, message: `${label || name} is required` }] : undefined}
          >
            <Input type="hidden" />
          </Form.Item>
          <Alert type="warning" showIcon message={NEXTGEN_PRACTICE_EMPTY_STATE} />
        </div>
      );
    }

    let inputNode;
    // Form.Item props a component needs beyond the defaults. Set by the case
    // that needs them so a field type's markup and its binding stay together,
    // rather than accreting `component === 'x'` ternaries on the Form.Item.
    let itemProps = {};
    switch (component) {
      case 'textarea':
        inputNode = <TextArea rows={3} />;
        break;
      // Renders the label beside the box rather than above it, so a boolean
      // flag reads as one line instead of an empty-looking labelled field.
      // The help icon rides along inside the box's own label, because the
      // Form.Item label — the usual home for help_text — is suppressed here.
      case 'checkbox':
        itemProps = {
          label: null,
          valuePropName: 'checked',
          // Redundant with valuePropName under antd 4 — rc-field-form already
          // reads target[valuePropName] — but stated explicitly so the boolean
          // contract does not depend on that default surviving an antd upgrade.
          getValueFromEvent: (e) => !!e?.target?.checked,
        };
        inputNode = (
          <Checkbox
            onChange={(e) => captureAnalytics('ehr_drawer_checkbox_toggled', {
              field: name,
              emr: schema?.emr,
              workflow,
              checked: !!e?.target?.checked,
            })}
          >
            {label}
            {helpText?.trim() && (
              <>
                {' '}
                {renderCheckboxHelpIcon(helpText.trim())}
              </>
            )}
          </Checkbox>
        );
        break;
      case 'date':
        inputNode = (
          <DatePicker
            style={{ width: '100%' }}
            format="MM/DD/YYYY"
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          />
        );
        break;
      case 'select': {
        const isDynamicDependent = !!field.dynamic_options_source;
        const dependencyValue = isDynamicDependent
          ? form.getFieldValue(field.dynamic_options_source.depends_on)
          : true;
        const isDisabled = isDynamicDependent && !dependencyValue;
        const isLoadingDynamic = isDynamicDependent && dynamicLoading[name];
        const autoAssignPlaceholder = autoAssignPlaceholderFor(field);
        const placeholder = autoAssignPlaceholder || field.placeholder;

        inputNode = (
          <Select
            showSearch
            style={{ width: '100%' }}
            optionFilterProp="children"
            optionLabelProp="children"
            allowClear
            loading={isLoadingDynamic}
            disabled={isDisabled || isLoadingDynamic}
            placeholder={isLoadingDynamic ? 'Loading options...' : (isDisabled ? (placeholder || 'Select...') : placeholder)}
            value={form.getFieldValue(name)}
            onChange={(val) => {
              const nextgenPracticeChanged = schema?.emr === 'NextGen' && name === 'location';
              if (nextgenPracticeChanged) {
                patientSearchResetRef.current();
              }
              applyFieldValues({ [name]: val });
              if (nextgenPracticeChanged) {
                captureAnalytics('nextgen_practice_selected', { workflow });
              }
            }}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          >
            {fieldOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );
        break;
      }
      case 'multi_select':
        inputNode = (
          <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            optionFilterProp="children"
            optionLabelProp="children"
            allowClear
            value={form.getFieldValue(name)}
            onChange={(vals) => applyFieldValues({ [name]: vals })}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          >
            {fieldOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );
        break;
      case 'emr_provider_autocomplete':
        inputNode = (
          <EmrProviderAutocomplete
            value={form.getFieldValue(name)}
            onChange={(val) => applyFieldValues({ [name]: val })}
            placeholder={field.placeholder || 'Search providers...'}
          />
        );
        break;
      case NEXTGEN_METADATA_COMPONENT:
        inputNode = (
          <NextgenDocumentMetadataFields
            credentialId={selectedCredential?.id}
            practiceId={formValues.location}
            dateOfServicePrefill={prefill.date_of_service}
            documentTitle={slot}
            onAnalytics={captureAnalytics}
            onChange={(value) => applyFieldValues({ [name]: value })}
          />
        );
        break;
      default: {
        if (!KNOWN_FREE_TEXT_COMPONENTS.includes(component)) {
          // The warning is emitted once per schema load, not here: this branch
          // re-runs on every keystroke that re-renders the drawer.
          return (
            <Alert
              key={name}
              type="error"
              showIcon
              className={fieldClassNames.join(' ')}
              message={`This integration field cannot be displayed (${component}).`}
            />
          );
        }

        // Suggestion fields (e.g. document_title suggesting the titles
        // configured for the selected document_category): render an
        // autocomplete offering the suggestions while still accepting free
        // text, otherwise fall through to a plain input.
        const suggestedOptions = field.suggested_options
          ? suggestedOptionsFor(
            field,
            options,
            form.getFieldValue(field.suggested_options.depends_on),
          )
          : null;

        if (suggestedOptions && suggestedOptions.length) {
          inputNode = (
            <AutoComplete
              style={{ width: '100%' }}
              allowClear
              placeholder={field.placeholder}
              value={form.getFieldValue(name)}
              onChange={(val) => applyFieldValues({ [name]: val })}
              options={suggestedOptions.map((option) => ({ value: option.value, label: option.label }))}
              filterOption={(inputValue, option) => (
                String(option.value).toLowerCase().includes(String(inputValue).toLowerCase())
              )}
              getPopupContainer={() => document.body}
              dropdownClassName="ehr-select-dropdown"
            />
          );
        } else if (field.input_mask === 'phone') {
          inputNode = <EhrPhoneInput placeholder={field.placeholder} />;
        } else {
          inputNode = <Input placeholder={field.placeholder} />;
        }
        break;
      }
    }

    // Get flex style from width
    const formItemStyle = { flex: getFieldFlexStyle(width) };

    // Build validation rules
    const rules = [];
    if (required) {
      rules.push({ required: true, message: `${label || name} is required` });
    }
    if (validation?.pattern) {
      const patternMessage = validation?.error_message || `${label || name} format is invalid`;
      rules.push({
        pattern: new RegExp(validation.pattern),
        message: patternMessage,
      });
    }
    if (validation?.max_length) {
      rules.push({
        max: validation.max_length,
        message: `${label || name} must be ${validation.max_length} characters or less`,
      });
    }

    // Enable real-time validation for fields with patterns (email, phone, etc.)
    const validateTrigger = validation?.pattern ? ['onChange', 'onBlur'] : ['onBlur'];

    return (
      <Form.Item
        key={name}
        name={name}
        label={component === 'checkbox' ? null : (renderFieldLabel(field) || label)}
        valuePropName={component === 'checkbox' ? 'checked' : 'value'}
        rules={rules}
        className={fieldClassNames.join(' ')}
        style={formItemStyle}
        colon={false}
        preserve={false}
        validateTrigger={validateTrigger}
        // Set by the switch case above; overrides the defaults it names.
        {...itemProps}
      >
        {inputNode}
      </Form.Item>
    );
  };

  // Helper to render a row of fields
  // Everything an owning component needs to render a field it has adopted, as
  // plain data. Deliberately not a React node: where and how the control appears
  // is the owner's business, and the drawer keeps only the form binding.
  const adoptedFieldSlot = (field) => ({
    name: field.internal_name,
    label: renderFieldLabel(field) || field.label,
    placeholder: field.placeholder,
    value: form.getFieldValue(field.internal_name),
    onChange: (value) => applyFieldValues({ [field.internal_name]: value }),
  });

  // An adopted field keeps its value in the form; its owner renders the control
  // the user actually sees.
  const renderAdoptedField = (field) => (
    <Form.Item key={field.internal_name} name={field.internal_name} noStyle preserve>
      <Input type="hidden" />
    </Form.Item>
  );

  const renderFieldRow = (fieldsInRow, key) => {
    if (!fieldsInRow || fieldsInRow.length === 0) return null;

    const rowClass = getRowClass(fieldsInRow);
    // The drawer's whole part in a declared pairing: work out which field owns
    // which, hand the owner the slot, and stay out of the layout it builds.
    const { adoptedByOwner, adoptedNames } = resolveFieldSlots(fieldsInRow);

    return (
      <div className={rowClass} key={key}>
        {fieldsInRow.map((field) => {
          if (adoptedNames.has(field.internal_name)) return renderAdoptedField(field);

          const adopted = adoptedByOwner[field.internal_name];
          return renderField(field, { slot: adopted ? adoptedFieldSlot(adopted) : undefined });
        })}
      </div>
    );
  };

  // Helper to get rows from subsection
  const getSubsectionRows = (subsection) => {
    return subsection.rows || [];
  };

  // Get sections directly from schema (backend sends fully structured data)
  const groupedFields = useMemo(() => {
    if (!schema?.sections) return [];
    return schema.sections;
  }, [schema]);

  const sectionStructure = useMemo(() => {
    return groupedFields.map((section, sectionIndex) => {
      const baseId = `section-${sectionIndex}`;
      const sectionId = section.title
        ? `${baseId}-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
        : baseId;

      const subsections = section.subsections.map((subsection, subsectionIndex) => {
        const subsectionBase = `${sectionId}-subsection-${subsectionIndex}`;
        const subsectionId = subsection.title
          ? `${subsectionBase}-${subsection.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
          : subsectionBase;

        return {
          ...subsection,
          id: subsectionId,
        };
      });

      return {
        ...section,
        id: sectionId,
        subsections,
      };
    });
  }, [groupedFields]);

  const taskFieldNames = useMemo(() => taskFieldNamesForSections(sectionStructure), [sectionStructure]);

  const taskDefaultValues = useMemo(() => {
    const defaults = {};
    taskFieldsForSections(sectionStructure).forEach((field) => {
      if (field.internal_name && field.default_value !== undefined) {
        defaults[field.internal_name] = field.default_value;
      }
    });
    return defaults;
  }, [sectionStructure]);

  const hiddenFields = useMemo(() => {
    const fieldsByName = new Map();
    sectionStructure.forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          (row || []).forEach((field) => {
            const name = field?.internal_name;
            if (name && isHiddenField(field) && !fieldsByName.has(name)) {
              fieldsByName.set(name, field);
            }
          });
        });
      });
    });

    return Array.from(fieldsByName.values());
  }, [sectionStructure]);

  const embeddedTaskAvailable = useMemo(() => (
    TASK_EMBEDDING_WORKFLOWS.includes(workflow)
      && sectionStructure.some((section) => (
        isTaskSchemaEntry(section)
          || (section.subsections || []).some((subsection) => isTaskSchemaEntry(subsection))
      ))
  ), [sectionStructure, workflow]);

  const visibleSectionStructure = useMemo(() => {
    return sectionStructure.reduce((visibleSections, section) => {
      const shouldGateTaskSections = embeddedTaskAvailable;
      const sectionIsTask = isTaskSchemaEntry(section);
      if (shouldGateTaskSections && sectionIsTask && !taskRequested) {
        return visibleSections;
      }

      const subsections = (section.subsections || []).reduce((visibleSubsections, subsection) => {
        if (shouldGateTaskSections && isTaskSchemaEntry(subsection) && !taskRequested) {
          return visibleSubsections;
        }

        const rows = visibleRowsForSubsection(subsection);
        if (rows.length === 0) {
          return visibleSubsections;
        }

        visibleSubsections.push({
          ...subsection,
          rows,
        });
        return visibleSubsections;
      }, []);

      if (subsections.length === 0) {
        return visibleSections;
      }

      visibleSections.push({
        ...section,
        subsections,
      });
      return visibleSections;
    }, []);
  }, [embeddedTaskAvailable, sectionStructure, taskRequested]);

  const requiredVisibleFields = useMemo(
    () => requiredFieldsForSections(visibleSectionStructure),
    [visibleSectionStructure],
  );

  const missingRequiredVisibleField = useMemo(() => (
    requiredVisibleFields.some(field => !hasPresentValue(formValues[field.internal_name]))
  ), [formValues, requiredVisibleFields]);

  const patientSearchSettings = useMemo(() => {
    if (!patientSearchConfig) {
      return null;
    }

    return {
      minChars: patientSearchConfig.min_chars ?? 2,
      debounceMs: patientSearchConfig.debounce_ms,
      limit: patientSearchConfig.result_limit ?? 10,
      fieldMappings: patientSearchConfig.field_mappings || {},
      searchFields: patientSearchConfig.search_fields || [],
      requiredGroups: patientSearchConfig.required_groups || [],
    };
  }, [patientSearchConfig]);

  const patientSearchEnabled = Boolean(patientSearchSettings) && Boolean(selectedCredential?.id);

  const {
    fieldValues: patientFieldValues,
    setFieldValue: setPatientFieldValue,
    results: patientSearchResults,
    isLoading: patientSearchLoading,
    error: patientSearchError,
    canSearch: patientSearchCanSearch,
    reset: resetPatientSearch,
  } = useEmrPatientSearch({
    credentialId: selectedCredential?.id,
    enabled: patientSearchEnabled,
    searchFields: patientSearchSettings?.searchFields,
    minQueryLength: patientSearchSettings?.minChars,
    debounceMs: patientSearchSettings?.debounceMs,
    limit: patientSearchSettings?.limit,
    requiredGroups: patientSearchSettings?.requiredGroups,
    location: formValues.location,
  });

  useEffect(() => {
    if (!patientSearchEnabled) {
      resetPatientSearch();
      setSelectedPatient(null);
      patientIdentifierOverridesRef.current = {};
    }
  }, [patientSearchEnabled, resetPatientSearch]);

  useEffect(() => {
    if (allowResultSelection) {
      return;
    }

    setSelectedPatient(null);
    patientIdentifierOverridesRef.current = {};
  }, [allowResultSelection]);

  useEffect(() => {
    resetPatientSearch();
    setSelectedPatient(null);
    patientIdentifierOverridesRef.current = {};
  }, [selectedCredential?.id, resetPatientSearch]);

  const clearPatientSelectionState = useCallback(() => {
    setSelectedPatient(null);
    patientIdentifierOverridesRef.current = {};
  }, []);

  const resolveMappingPath = useCallback((source, rawPath) => {
    if (!rawPath) {
      return undefined;
    }

    const segments = Array.isArray(rawPath) ? rawPath : rawPath.toString().split('.');
    return segments.reduce((acc, key) => {
      if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
        return acc[key];
      }
      return undefined;
    }, source);
  }, []);

  const mapPatientToFields = useCallback((patient) => {
    patientIdentifierOverridesRef.current = {};

    if (!patient) {
      return {};
    }

    const hydratedPatient = {
      ...patient,
      metadata: patient?.metadata || {},
    };

    const mappedFromConfig = Object.entries(patientSearchSettings?.fieldMappings || {}).reduce((acc, [fieldName, path]) => {
      const rawValue = resolveMappingPath(hydratedPatient, path);
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        acc[fieldName] = undefined;
        return acc;
      }

      const fieldDefinition = fieldDefinitions[fieldName];
      let valueToAssign = rawValue;

      if (fieldDefinition?.component === 'date') {
        const momentValue = moment(rawValue, [moment.ISO_8601, 'MM/DD/YYYY'], true);
        valueToAssign = momentValue.isValid() ? momentValue : rawValue;
      }

      if (fieldName === 'patient_identifier') {
        const patientId = rawValue;
        const mrnValue = extractPatientMrn(hydratedPatient);
        const displayValue = derivePatientIdentifierDisplay(patientId, mrnValue);

        valueToAssign = displayValue;

        if (patientIdentifierOverridesRef.current && patientId !== undefined && patientId !== null) {
          patientIdentifierOverridesRef.current[fieldName] = {
            id: patientId,
            display: displayValue,
          };
        }
      }

      acc[fieldName] = valueToAssign;
      return acc;
    }, {});

    return applyPatientFallbacks(patient, mappedFromConfig, fieldDefinitions, patientIdentifierOverridesRef);
  }, [patientSearchSettings, resolveMappingPath, fieldDefinitions, patientIdentifierOverridesRef]);

  const clearSelectedPatientFormFields = useCallback((patient) => {
    const updates = mapPatientToFields(patient);
    const fieldsToClear = clearValuesForFields(updates);
    if (Object.keys(fieldsToClear).length > 0) {
      applyFieldValues(fieldsToClear);
    }
  }, [form, mapPatientToFields]);

  const handlePatientSearchFieldChange = useCallback((key, value) => {
    if (allowResultSelection && (selectedPatient || Object.keys(patientIdentifierOverridesRef.current || {}).length > 0)) {
      if (selectedPatient) {
        clearSelectedPatientFormFields(selectedPatient);
      }
      clearPatientSelectionState();
    }

    setPatientFieldValue(key, value);
  }, [allowResultSelection, clearPatientSelectionState, clearSelectedPatientFormFields, selectedPatient, setPatientFieldValue]);

  const handlePatientSearchReset = useCallback(() => {
    if (allowResultSelection && (selectedPatient || Object.keys(patientIdentifierOverridesRef.current || {}).length > 0)) {
      if (selectedPatient) {
        clearSelectedPatientFormFields(selectedPatient);
      }
      clearPatientSelectionState();
    }

    resetPatientSearch();
  }, [allowResultSelection, clearPatientSelectionState, clearSelectedPatientFormFields, resetPatientSearch, selectedPatient]);

  useEffect(() => {
    patientSearchResetRef.current = handlePatientSearchReset;
  }, [handlePatientSearchReset]);

  const handlePatientSelect = useCallback((patient) => {
    if (!allowResultSelection || !patient) {
      return;
    }

    setSelectedPatient(patient);
    patientIdentifierOverridesRef.current = {};
    const updates = mapPatientToFields(patient);
    if (Object.keys(updates).length > 0) {
      applyFieldValues(updates);
      // Flash the fields that were updated
      const fieldsToFlash = Object.keys(updates).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setFlashingFields(fieldsToFlash);
      // Clear flashing after animation completes
      setTimeout(() => {
        setFlashingFields({});
      }, 700);
      message.success('Patient details applied to the form.');
    }
  }, [allowResultSelection, form, mapPatientToFields]);

  const handleClearPatient = useCallback((e) => {
    // Prevent the collapse from toggling when clicking the clear button
    e.stopPropagation();

    if (!allowResultSelection || !selectedPatient) {
      return;
    }

    // Clear the form fields
    clearSelectedPatientFormFields(selectedPatient);

    // Clear the selected patient and identifier overrides
    setSelectedPatient(null);
    patientIdentifierOverridesRef.current = {};

    message.info('Patient selection cleared.');
  }, [allowResultSelection, clearSelectedPatientFormFields, selectedPatient]);

  const workflowDisplayTitle = schema?.metadata?.title || modalTitle;
  const workflowDescription = schema?.metadata?.description;
  const patientSelectionMissing = requiresPatientResultSelection && !selectedPatient;
  const submitDisabled = fetchingCredentials
    || loading
    || submitting
    || !schema
    || !selectedCredential
    || missingRequiredVisibleField
    || patientSelectionMissing;

  const renderPatientSearch = () => {
    if (!patientSearchEnabled) {
      return null;
    }

    const selectedPatientName = allowResultSelection ? (selectedPatient?.label || selectedPatient?.metadata?.patient_name) : null;
    const selectedPatientMrn = allowResultSelection ? (selectedPatient?.mrn || selectedPatient?.metadata?.mrn) : null;
    const patientSearchDescription = patientSearchConfig?.description || (
      allowResultSelection
        ? 'Find an existing patient in the EMR before submitting.'
        : 'Search for existing patients in the EMR. Results are informational only and will not populate the form.'
    );

    const collapseHeader = (
      <div className="dynamic-ehr-drawer__collapse-header">
        <div className="dynamic-ehr-drawer__card-icon">
          <SearchOutlined />
        </div>
        <div className="dynamic-ehr-drawer__card-header-content">
          <div className="dynamic-ehr-drawer__card-title">Search Patients</div>
          {selectedPatientName ? (
            <div className="dynamic-ehr-drawer__card-selection">
              Selected: <strong>{selectedPatientName}</strong>
              {selectedPatientMrn && <span className="dynamic-ehr-drawer__card-selection-mrn"> (MRN: {selectedPatientMrn})</span>}
              <button
                type="button"
                className="dynamic-ehr-drawer__card-selection-clear"
                onClick={handleClearPatient}
                aria-label="Clear patient selection"
              >
                <CloseOutlined />
              </button>
            </div>
          ) : (
            <div className="dynamic-ehr-drawer__card-description">
              {patientSearchDescription}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <Collapse
        className="dynamic-ehr-drawer__collapse"
        bordered={false}
        expandIconPosition="start"
        defaultActiveKey={['patient-search']}
      >
        <Collapse.Panel header={collapseHeader} key="patient-search">
          <PatientSearchPanel
            searchFields={patientSearchSettings?.searchFields}
            searchInstructions={patientSearchConfig?.search_instructions}
            fieldValues={patientFieldValues}
            onFieldChange={handlePatientSearchFieldChange}
            onReset={handlePatientSearchReset}
            results={patientSearchResults}
            isLoading={patientSearchLoading}
            error={patientSearchError}
            canSearch={patientSearchCanSearch}
            allowResultSelection={allowResultSelection}
            onSelect={handlePatientSelect}
            selectedPatient={allowResultSelection ? selectedPatient : null}
          />
        </Collapse.Panel>
      </Collapse>
    );
  };

  const renderCredentialSwitcher = () => {
    if (credentials.length <= 1) {
      return null;
    }

    return (
      <Select
        className="dynamic-ehr-drawer__credential-select"
        value={selectedCredential?.id}
        onChange={(id) => {
          const credential = credentials.find(c => c.id === id);
          setSelectedCredential(credential || { id });
        }}
        options={credentials.map(credential => ({
          value: credential.id,
          label: credential.nickname || credential.emr_name || `Credential ${credential.id}`,
        }))}
      />
    );
  };

  const handleTaskRequestedChange = (e) => {
    const checked = e.target.checked;
    setTaskRequested(checked);

    if (checked) {
      const defaultsToApply = Object.entries(taskDefaultValues).reduce((acc, [fieldName, defaultValue]) => {
        const currentValue = form.getFieldValue(fieldName);
        if (currentValue === undefined || currentValue === null || currentValue === '') {
          acc[fieldName] = defaultValue;
        }
        return acc;
      }, {});

      if (Object.keys(defaultsToApply).length > 0) {
        applyFieldValues(defaultsToApply);
      }
      return;
    }

    if (taskFieldNames.size === 0) {
      return;
    }

    const clearedValues = Array.from(taskFieldNames).reduce((acc, fieldName) => {
      acc[fieldName] = undefined;
      return acc;
    }, {});
    applyFieldValues(clearedValues);
    setCrossFieldErrors((prevErrors) => (
      prevErrors.filter((err) => !err.fields.some((field) => taskFieldNames.has(field)))
    ));
  };

  const renderTaskToggle = () => {
    if (!embeddedTaskAvailable) {
      return null;
    }

    return (
      <div className="dynamic-ehr-drawer__task-toggle">
        <Checkbox checked={taskRequested} onChange={handleTaskRequestedChange}>
          Create Task
        </Checkbox>
      </div>
    );
  };

  const sectionDescriptions = useMemo(() => schema?.metadata?.section_descriptions || {}, [schema]);
  const submitLabel = schema?.metadata?.submit_label || 'Submit';

  return (
    <Drawer
      title={null}
      visible={visible}
      onClose={handleClose}
      afterVisibleChange={handleAfterVisibleChange}
      destroyOnClose={false}
      forceRender
      width={620}
      closable={false}
      mask={false}
      placement="left"
      className="ehr-side-panel-drawer"
      zIndex={2000}
      bodyStyle={{ padding: 0 }}
      footer={
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitDisabled}
          >
            {submitLabel}
          </Button>
        </div>
      }
    >
      {fetchingCredentials || loading || !schema ? (
        <div className="dynamic-ehr-drawer__loading">
          <Spin size="large" tip={fetchingCredentials ? 'Loading credentials...' : 'Loading form...'} />
        </div>
      ) : (
        <div className="dynamic-ehr-drawer">
          <div className="dynamic-ehr-drawer__header">
            <div className="dynamic-ehr-drawer__header-text">
              <div className="dynamic-ehr-drawer__header-title">
                <span className="dynamic-ehr-drawer__header-icon">{getWorkflowIcon(workflow, workflowDisplayTitle)}</span>
                {workflowDisplayTitle}
              </div>
              {workflowDescription && (
                <div className="dynamic-ehr-drawer__header-subtitle">
                  {workflowDescription}
                </div>
              )}
            </div>
            <div className="dynamic-ehr-drawer__header-actions">
              {renderCredentialSwitcher()}
              <button
                type="button"
                className="dynamic-ehr-drawer__close"
                onClick={handleClose}
                aria-label="Close"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
          <div className="dynamic-ehr-drawer__body">
            {renderPatientSearch()}
            {crossFieldErrors.length > 0 && (
              <Alert
                type="warning"
                showIcon
                className="dynamic-ehr-drawer__cross-field-alert"
                message={crossFieldErrors.map((err) => err.message).join('; ')}
              />
            )}
            {renderTaskToggle()}
            <Form
              form={form}
              layout="vertical"
              className="dynamic-ehr-drawer__form"
              onValuesChange={handleValuesChange}
            >
              {hiddenFields.map(field => renderField(field))}
              {visibleSectionStructure.map((section) => {
                const sectionTitle = section.title || 'Additional Details';
                const description = sectionDescriptions[section.title] || null;

                return (
                  <section
                    key={section.id}
                    id={section.id}
                    className="dynamic-ehr-drawer__card"
                  >
                    <div className="dynamic-ehr-drawer__card-header">
                      <div className="dynamic-ehr-drawer__card-icon">
                        {getSectionIcon(sectionTitle)}
                      </div>
                      <div className="dynamic-ehr-drawer__card-header-content">
                        <div className="dynamic-ehr-drawer__card-title">{sectionTitle}</div>
                        {description && (
                          <div className="dynamic-ehr-drawer__card-description">{description}</div>
                        )}
                      </div>
                    </div>
                    <div className="dynamic-ehr-drawer__card-body">
                      {section.subsections.map((subsection) => (
                        <div key={subsection.id} className="dynamic-ehr-drawer__subsection">
                          {subsection.title && (
                            <div className="dynamic-ehr-drawer__subsection-heading">
                              {subsection.title}
                            </div>
                          )}
                          {getSubsectionRows(subsection).map((row, rowIndex) => (
                            renderFieldRow(row, `${subsection.id}-row-${rowIndex}`)
                          ))}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </Form>
          </div>
        </div>
      )}
    </Drawer>
  );
};

DynamicEhrDrawer.propTypes = {
  visible: PropTypes.bool,
  drawerData: PropTypes.shape({
    emrIntegrationCredentialId: PropTypes.number,
    workflow: PropTypes.string,
    prefill: PropTypes.object,
    parentType: PropTypes.string,
    sendToken: PropTypes.string,
    title: PropTypes.string,
    providerHints: PropTypes.shape({
      orderingProviderName: PropTypes.string,
      referringProviderName: PropTypes.string,
    }),
  }),
  onClose: PropTypes.func.isRequired,
  createChartUpload: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  allDocumentCategories: PropTypes.object,
};

DynamicEhrDrawer.defaultProps = {
  visible: false,
  drawerData: null,
  allDocumentCategories: {},
};

export { DynamicEhrDrawer as UnconnectedDynamicEhrDrawer };

const mapStateToProps = (state) => ({
  allDocumentCategories: state.userProfile.allDocumentCategories,
});

export default connect(mapStateToProps, {
  createChartUpload,
  getDocumentCategories,
})(DynamicEhrDrawer);
